import type { WorkJournal } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { Button } from '../../ui/button';
import { EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { Field, Input, Select } from '../../ui/form';
import { Page, PageHeader, Panel } from '../../ui/layout';
import { useProjectDefault, useProjectsWith } from '../psd/shared';

/** Дата без часового пояса: «2026-10-25» → «25.10.2026» */
const date = (v: string) => v.split('-').reverse().join('.');

type Search = { project?: string; object?: string; from?: string; to?: string };

/** Общий журнал работ (ДК-07): записи собираются из принятых отчётов площадки, править их здесь нельзя */
export function WorkJournalPage() {
  const search = useSearch({ strict: false }) as Search;
  const navigate = useNavigate();
  const { list } = useProjectsWith('documents.view');
  const projectId = search.project ?? '';
  const go = (next: Search) => navigate({ to: '/documents/journals', search: next as never, replace: true });
  useProjectDefault(list, undefined, projectId, (id) => void go({ project: id }));
  const project = list.find((p) => p.id === projectId);
  useCrumbs([{ label: 'Документы', to: '/documents' }, { label: 'Журнал работ' }]);
  const q = useQuery({
    queryKey: ['work-journal', projectId, search.object ?? '', search.from ?? '', search.to ?? ''],
    queryFn: () =>
      api<WorkJournal>('/documents/journals/work', {
        query: { projectId, objectId: search.object, from: search.from, to: search.to },
      }),
    enabled: Boolean(projectId),
    retry: false,
  });
  const j = q.data;
  const objects = project?.objects ?? [];
  return (
    <Page className="max-w-6xl">
      <PageHeader
        title="Общий журнал работ"
        subtitle="Что сделано на площадке: записи берутся из принятых отчётов смены"
        actions={
          <div className="flex flex-wrap gap-x-5 print:hidden">
            <Link to="/documents/exec" className="inline-flex min-h-11 items-center underline">
              Исполнительная документация
            </Link>
            <Button variant="secondary" onClick={() => window.print()} disabled={!j?.entries.length}>
              Напечатать
            </Button>
          </div>
        }
      />
      <div className="grid gap-5">
        <Panel title="Что показать" className="print:hidden">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Проект" htmlFor="wj-project">
              <Select
                id="wj-project"
                className="!h-11"
                value={projectId}
                onChange={(e) => void go({ project: e.target.value || undefined })}
              >
                {list.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code} {p.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Объект" htmlFor="wj-object">
              <Select
                id="wj-object"
                className="!h-11"
                value={search.object ?? ''}
                onChange={(e) => void go({ ...search, object: e.target.value || undefined })}
              >
                <option value="">Все объекты</option>
                {objects.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="С даты" htmlFor="wj-from">
              <Input
                id="wj-from"
                type="date"
                className="!h-11"
                value={search.from ?? j?.from ?? ''}
                onChange={(e) => void go({ ...search, from: e.target.value || undefined })}
              />
            </Field>
            <Field label="По дату" htmlFor="wj-to">
              <Input
                id="wj-to"
                type="date"
                className="!h-11"
                value={search.to ?? j?.to ?? ''}
                onChange={(e) => void go({ ...search, to: e.target.value || undefined })}
              />
            </Field>
          </div>
        </Panel>
        {q.isPending && projectId ? <Spinner /> : null}
        {q.error ? <ErrorBox error={q.error} /> : null}
        {j ? (
          <Panel title={`Журнал: ${j.projectCode} ${j.projectName}`} meta={`${date(j.from)} — ${date(j.to)}`}>
            <p className="mb-3 text-sm text-ink-2">
              Принятых отчётов: {j.reportsCount}. Дней без записей: {j.daysWithoutEntries}.
              {j.pendingReports
                ? ` Ещё на проверке: ${j.pendingReports} — в журнал попадут после принятия.`
                : ''}
            </p>
            {j.entries.length ? (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[56rem] border-collapse text-left text-sm">
                    <thead>
                      <tr className="border-b border-line text-ink-2">
                        <th className="p-2">№</th>
                        <th className="p-2">Дата, смена</th>
                        <th className="p-2">Вид работ</th>
                        <th className="p-2">Объём</th>
                        <th className="p-2">Люди / техника / фото</th>
                        <th className="p-2">Участок, исполнитель</th>
                        <th className="p-2">Примечание</th>
                      </tr>
                    </thead>
                    <tbody>
                      {j.entries.map((e) => (
                        <tr key={`${e.reportId}-${e.lineId}`} className="border-b border-line align-top">
                          <td className="p-2">{e.no}</td>
                          <td className="p-2">
                            {date(e.day)}, {e.shift}
                          </td>
                          <td className="p-2 [overflow-wrap:anywhere]">
                            {e.activityName}
                            {e.objectName ? <span className="block text-ink-2">{e.objectName}</span> : null}
                          </td>
                          <td className="p-2">
                            {e.quantity} {e.unit}
                            {e.claimed !== e.quantity ? (
                              <span className="block text-ink-2">заявлено {e.claimed}</span>
                            ) : null}
                          </td>
                          <td className="p-2">
                            {e.people} / {e.machines} / {e.photos}
                          </td>
                          <td className="p-2">
                            {e.packageName}
                            <span className="block text-ink-2">{e.authorName}</span>
                          </td>
                          <td className="p-2 [overflow-wrap:anywhere]">{e.note ?? ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <ul className="grid gap-3 md:hidden">
                  {j.entries.map((e) => (
                    <li key={`${e.reportId}-${e.lineId}`} className="rounded-md border border-line p-3">
                      <p className="font-medium [overflow-wrap:anywhere]">
                        {e.no}. {e.activityName}
                      </p>
                      <p className="mt-1 text-sm">
                        {e.quantity} {e.unit}
                        {e.claimed !== e.quantity ? ` (заявлено ${e.claimed})` : ''}
                      </p>
                      <p className="mt-1 text-sm text-ink-2">
                        {date(e.day)}, {e.shift} · {e.packageName} · {e.authorName}
                      </p>
                      <p className="text-sm text-ink-2">
                        Людей: {e.people} · техники: {e.machines} · фото: {e.photos}
                      </p>
                      {e.note ? <p className="mt-1 text-sm [overflow-wrap:anywhere]">{e.note}</p> : null}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <EmptyState title="За выбранный период принятых отчётов смены нет." />
            )}
          </Panel>
        ) : null}
      </div>
    </Page>
  );
}
